import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  Download,
  Eye,
  MoreHorizontal,
  Pencil,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { BuyLogTab } from "@/components/buy-log/BuyLogTab";
import { SoVTab } from "@/components/buy-log/SoVTab";
import { PcoTab } from "@/components/change-orders/PcoTab";
import { GcCostSetupTab } from "@/components/gc-line-items/GcCostSetupTab";
import { GrSovTab } from "@/components/gr-invoices/GrSovTab";
import { AddExistingContactDialog } from "@/components/projects/AddExistingContactDialog";
import { DocumentsTab } from "@/components/projects/DocumentsTab";
import { MilestonesTab } from "@/components/projects/MilestonesTab";
import { OverviewTab } from "@/components/projects/overview/OverviewTab";
import { PayApplicationsTab } from "@/components/projects/PayApplicationsTab";
import type { NavBadges } from "@/components/projects/ProjectDetailNav";
import { ProjectDetailNav } from "@/components/projects/ProjectDetailNav";
import { ProjectSitePhotosTab } from "@/components/projects/ProjectSitePhotosTab";
import { ProjectDirectoryPanel, TeamTab } from "@/components/projects/TeamTab";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { ErrorState } from "@/components/shared/error-state";
import { TasksTab } from "@/components/tasks/TasksTab";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { PlaceResult } from "@/components/ui/PlacesAutocompleteInput";
import { PlacesAutocompleteInput } from "@/components/ui/PlacesAutocompleteInput";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { useBuyLogSummary } from "@/hooks/use-budgets";
import { useChangeOrdersForProject } from "@/hooks/use-change-orders";
import type { Commitment } from "@/hooks/use-commitments";
import { useCommitments, useDeleteCommitment } from "@/hooks/use-commitments";
import { useCompanies } from "@/hooks/use-companies";
import type { Contact } from "@/hooks/use-contacts";
import {
  useAddNewContactToProject,
  useContacts,
  useProjectContacts,
  useRemoveProjectContact,
  useUpdateContact,
} from "@/hooks/use-contacts";
import { useProjectMilestones } from "@/hooks/use-milestones";
import { useProjectContractors } from "@/hooks/use-project-contractors";
import type { Project } from "@/hooks/use-projects";
import { useDeleteProject, useProject, useUpdateProject } from "@/hooks/use-projects";
import type { Rfi } from "@/hooks/use-rfis";
import { useRfis } from "@/hooks/use-rfis";
import type { Submittal } from "@/hooks/use-submittals";
import { useSubmittals } from "@/hooks/use-submittals";
import { useProjectTaskDocumentCount } from "@/hooks/use-task-documents";
import { useProjectTasks } from "@/hooks/use-tasks";
import { useSession } from "@/lib/auth-client";

// ── Design System Helpers ──

const statusConfig: Record<string, { bg: string; text: string; label: string }> = {
  active: { bg: "bg-green-100", text: "text-green-700", label: "Active" },
  open: { bg: "bg-green-100", text: "text-green-700", label: "Open" },
  approved: { bg: "bg-green-100", text: "text-green-700", label: "Approved" },
  draft: { bg: "bg-gray-100", text: "text-gray-600", label: "Draft" },
  pending: { bg: "bg-amber-100", text: "text-amber-700", label: "Pending" },
  in_progress: { bg: "bg-amber-100", text: "text-amber-700", label: "In Progress" },
  not_started: { bg: "bg-gray-100", text: "text-gray-600", label: "Not Started" },
  on_hold: { bg: "bg-amber-100", text: "text-amber-700", label: "On Hold" },
  completed: { bg: "bg-blue-100", text: "text-blue-700", label: "Completed" },
  closed: { bg: "bg-blue-100", text: "text-blue-700", label: "Closed" },
  answered: { bg: "bg-blue-100", text: "text-blue-700", label: "Answered" },
  rejected: { bg: "bg-red-100", text: "text-red-700", label: "Rejected" },
  void: { bg: "bg-red-100", text: "text-red-700", label: "Void" },
  cancelled: { bg: "bg-red-100", text: "text-red-700", label: "Cancelled" },
  blocked: { bg: "bg-red-100", text: "text-red-700", label: "Blocked" },
  skipped: { bg: "bg-gray-100", text: "text-gray-500", label: "Skipped" },
  overdue: { bg: "bg-red-100", text: "text-red-700", label: "Overdue" },
};

function StatusBadge({ status }: { status: string | null }) {
  const key = status ?? "draft";
  const config = statusConfig[key] ?? { bg: "bg-gray-100", text: "text-gray-600", label: key };
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${config.bg} ${config.text}`}
    >
      {config.label}
    </span>
  );
}

function TypeBadge({ label }: { label: string | null }) {
  if (!label) return <span className="text-sm text-gray-400">--</span>;
  return (
    <span className="inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full border border-gray-200 text-gray-600">
      {label.replace(/_/g, " ")}
    </span>
  );
}

function ProgressBar({ percent }: { percent: number }) {
  const clamped = Math.max(0, Math.min(100, percent));
  return (
    <div className="w-full bg-gray-100 rounded-full h-2">
      <div
        className="bg-[#334155] h-2 rounded-full transition-all duration-300"
        style={{ width: `${clamped}%` }}
      />
    </div>
  );
}

function formatCurrency(value: string | null | undefined): string {
  if (!value) return "--";
  const num = Number(value);
  if (Number.isNaN(num)) return "--";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(num);
}

function formatDate(date: string | null | undefined): string {
  if (!date) return "--";
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// ── Overview Tab ──

function EditProjectDialog({
  project,
  open,
  onOpenChange,
}: {
  project: Project;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const updateProject = useUpdateProject();
  const deleteProject = useDeleteProject();
  const navigate = useNavigate();
  const { data: session } = useSession();
  const isAdmin = session?.user?.role === "admin";
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [name, setName] = useState(project.name);
  const [projectNumber, setProjectNumber] = useState(project.projectNumber ?? "");
  const type = project.type;
  const [status, setStatus] = useState(project.status);
  const [phase, setPhase] = useState(project.phase);
  const [location, setLocation] = useState(project.location ?? "");
  const [client, setClient] = useState(project.client ?? "");
  const [clientCompanyId, setClientCompanyId] = useState(project.clientCompanyId ?? "");
  const [clientSearch, setClientSearch] = useState("");
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const clientDropdownRef = useRef<HTMLDivElement>(null);
  const [superintendent, setSuperintendent] = useState(project.superintendent ?? "");
  const [superintendentId, setSuperintendentId] = useState("");
  const [projectManager, setProjectManager] = useState(project.projectManager ?? "");
  const [projectManagerId, setProjectManagerId] = useState("");
  const [startDate, setStartDate] = useState(project.startDate ?? "");
  const [endDate, setEndDate] = useState(project.endDate ?? "");
  const [contractValue, setContractValue] = useState(project.contractValue ?? "");
  const [description, setDescription] = useState(project.description ?? "");
  const [generalContractorCompanyId, setGeneralContractorCompanyId] = useState(
    project.generalContractorCompanyId ?? "",
  );
  const [contractDate, setContractDate] = useState(project.contractDate ?? project.startDate ?? "");
  const [contractFor, setContractFor] = useState(project.contractFor ?? "");
  const [addressLine1, setAddressLine1] = useState(project.addressLine1 ?? "");
  const [addressLine2, setAddressLine2] = useState(project.addressLine2 ?? "");
  const [city, setCity] = useState(project.city ?? "");
  const [stateValue, setStateValue] = useState(project.state ?? "");
  const [zip, setZip] = useState(project.zip ?? "");
  const { data: gcCompaniesData } = useCompanies("general_contractor");
  const gcCompanies = gcCompaniesData ?? [];
  const { data: clientCompaniesData } = useCompanies("owner");
  const clientCompanies = clientCompaniesData ?? [];
  const { data: contactsData } = useContacts();
  const contacts = (contactsData ?? []) as Contact[];

  const filteredClientCompanies = useMemo(() => {
    if (!clientSearch.trim()) return clientCompanies;
    const q = clientSearch.toLowerCase();
    return clientCompanies.filter((c) => c.companyName.toLowerCase().includes(q));
  }, [clientCompanies, clientSearch]);

  const gcContacts = useMemo(
    () =>
      generalContractorCompanyId
        ? contacts.filter((c) => c.companyId === generalContractorCompanyId)
        : contacts,
    [contacts, generalContractorCompanyId],
  );

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(e.target as Node)) {
        setShowClientDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    updateProject.mutate(
      {
        id: project.id,
        name,
        projectNumber: projectNumber || null,
        type,
        status,
        phase,
        location: location || null,
        client: client || null,
        clientCompanyId: clientCompanyId || null,
        superintendent: superintendent || null,
        projectManager: projectManager || null,
        startDate: startDate || null,
        endDate: endDate || null,
        contractValue: contractValue || null,
        description: description || null,
        generalContractorCompanyId: generalContractorCompanyId || null,
        contractDate: contractDate || null,
        contractFor: contractFor || null,
        addressLine1: addressLine1 || null,
        addressLine2: addressLine2 || null,
        city: city || null,
        state: stateValue || null,
        zip: zip || null,
      },
      { onSuccess: () => onOpenChange(false) },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-[#334155]">Edit Project</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5 col-span-2">
              <Label htmlFor="edit-name" className="text-sm font-medium text-gray-700">
                Name
              </Label>
              <Input
                id="edit-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-project-number" className="text-sm font-medium text-gray-700">
                Project Number
              </Label>
              <Input
                id="edit-project-number"
                value={projectNumber}
                onChange={(e) => setProjectNumber(e.target.value)}
                placeholder="e.g. PB-2025-001"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-status-top" className="text-sm font-medium text-gray-700">
                Status
              </Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger id="edit-status-top">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="on_hold">On Hold</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-phase" className="text-sm font-medium text-gray-700">
              Phase
            </Label>
            <Select value={phase} onValueChange={(v) => setPhase(v as typeof phase)}>
              <SelectTrigger id="edit-phase">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pre_construction">Pre-Construction</SelectItem>
                <SelectItem value="construction">Construction</SelectItem>
                <SelectItem value="post_construction">Post-Construction</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-location" className="text-sm font-medium text-gray-700">
              Location
            </Label>
            <PlacesAutocompleteInput
              value={location}
              onChange={setLocation}
              onSelect={(result: PlaceResult) => {
                setLocation(result.location);
                setAddressLine1(result.addressLine1);
                setCity(result.city);
                setStateValue(result.state);
                setZip(result.zip);
              }}
              inputClassName="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
            />
          </div>
          <div ref={clientDropdownRef} className="space-y-1.5 relative">
            <Label className="text-sm font-medium text-gray-700">Client</Label>
            <div className="relative">
              <input
                type="text"
                value={clientCompanyId ? client : clientSearch}
                onChange={(e) => {
                  if (clientCompanyId) {
                    setClient("");
                    setClientCompanyId("");
                  }
                  setClientSearch(e.target.value);
                  setShowClientDropdown(true);
                }}
                onFocus={() => setShowClientDropdown(true)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                placeholder="Search client companies..."
              />
              {clientCompanyId ? (
                <button
                  type="button"
                  onClick={() => {
                    setClient("");
                    setClientCompanyId("");
                    setClientSearch("");
                    setShowClientDropdown(true);
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              ) : (
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              )}
            </div>
            {showClientDropdown && !clientCompanyId && (
              <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {filteredClientCompanies.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-gray-500">No client companies found</div>
                ) : (
                  filteredClientCompanies.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 transition-colors"
                      onClick={() => {
                        setClient(c.companyName);
                        setClientCompanyId(c.id);
                        setClientSearch("");
                        setShowClientDropdown(false);
                      }}
                    >
                      {c.companyName}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-gc" className="text-sm font-medium text-gray-700">
              General Contractor
            </Label>
            <Select
              value={generalContractorCompanyId || "__none__"}
              onValueChange={(v) => {
                const newGc = v === "__none__" ? "" : v;
                setGeneralContractorCompanyId(newGc);
                setSuperintendentId("");
                setSuperintendent("");
                setProjectManagerId("");
                setProjectManager("");
              }}
            >
              <SelectTrigger id="edit-gc">
                <SelectValue placeholder="Select general contractor..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Not set</SelectItem>
                {gcCompanies.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.companyName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-400">
              Used as the default contractor for Owner Pay App cost codes (including Division 99).
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-pm" className="text-sm font-medium text-gray-700">
              Project Manager
            </Label>
            <select
              id="edit-pm"
              value={projectManagerId}
              onChange={(e) => {
                const selected = gcContacts.find((p) => p.id === e.target.value);
                setProjectManagerId(e.target.value);
                setProjectManager(selected ? `${selected.firstName} ${selected.lastName}` : "");
              }}
              disabled={!generalContractorCompanyId}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 disabled:bg-gray-50 disabled:text-gray-400"
            >
              <option value="">
                {generalContractorCompanyId
                  ? projectManager || "Select project manager..."
                  : "Select a GC first..."}
              </option>
              {gcContacts.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.firstName} {p.lastName}
                </option>
              ))}
            </select>
            {generalContractorCompanyId && gcContacts.length === 0 && (
              <p className="text-xs text-gray-400">No contacts found for this GC.</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-super" className="text-sm font-medium text-gray-700">
              Superintendent
            </Label>
            <select
              id="edit-super"
              value={superintendentId}
              onChange={(e) => {
                const selected = gcContacts.find((p) => p.id === e.target.value);
                setSuperintendentId(e.target.value);
                setSuperintendent(selected ? `${selected.firstName} ${selected.lastName}` : "");
              }}
              disabled={!generalContractorCompanyId}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 disabled:bg-gray-50 disabled:text-gray-400"
            >
              <option value="">
                {generalContractorCompanyId
                  ? superintendent || "Select superintendent..."
                  : "Select a GC first..."}
              </option>
              {gcContacts.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.firstName} {p.lastName}
                </option>
              ))}
            </select>
            {generalContractorCompanyId && gcContacts.length === 0 && (
              <p className="text-xs text-gray-400">No contacts found for this GC.</p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="edit-start" className="text-sm font-medium text-gray-700">
                Start Date
              </Label>
              <DatePicker
                value={startDate || null}
                onChange={(next) => setStartDate(next ?? "")}
                ariaLabel="Start date"
                className="w-full"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-end" className="text-sm font-medium text-gray-700">
                End Date
              </Label>
              <DatePicker
                value={endDate || null}
                onChange={(next) => setEndDate(next ?? "")}
                ariaLabel="End date"
                className="w-full"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-contract" className="text-sm font-medium text-gray-700">
              Contract Value
            </Label>
            <Input
              id="edit-contract"
              value={contractValue}
              onChange={(e) => setContractValue(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="edit-contract-date" className="text-sm font-medium text-gray-700">
                Contract Date
              </Label>
              <DatePicker
                value={contractDate || null}
                onChange={(next) => setContractDate(next ?? "")}
                ariaLabel="Contract date"
                className="w-full"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-contract-for" className="text-sm font-medium text-gray-700">
                Contract For
              </Label>
              <Input
                id="edit-contract-for"
                value={contractFor}
                onChange={(e) => setContractFor(e.target.value)}
                placeholder="General Construction"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-gray-700">Project Address</Label>
            <Input
              placeholder="Address line 1"
              value={addressLine1}
              onChange={(e) => setAddressLine1(e.target.value)}
            />
            <Input
              placeholder="Address line 2"
              value={addressLine2}
              onChange={(e) => setAddressLine2(e.target.value)}
            />
            <div className="grid grid-cols-3 gap-2">
              <Input placeholder="City" value={city} onChange={(e) => setCity(e.target.value)} />
              <Input
                placeholder="State"
                value={stateValue}
                onChange={(e) => setStateValue(e.target.value)}
              />
              <Input placeholder="ZIP" value={zip} onChange={(e) => setZip(e.target.value)} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-desc" className="text-sm font-medium text-gray-700">
              Description
            </Label>
            <Textarea
              id="edit-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            {isAdmin ? (
              <Button
                type="button"
                variant="destructive"
                className="mr-auto"
                onClick={() => setConfirmDeleteOpen(true)}
                disabled={deleteProject.isPending}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </Button>
            ) : null}
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={updateProject.isPending}
              className="bg-[#334155] hover:bg-[#415A77] text-white"
            >
              {updateProject.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title="Archive project?"
        description="This project will be hidden from the Projects list. Related data (commitments, pay apps, RFIs, etc.) is preserved and an admin can restore the project later."
        confirmLabel="Archive project"
        variant="destructive"
        loading={deleteProject.isPending}
        onConfirm={async () => {
          await deleteProject.mutateAsync(project.id);
          setConfirmDeleteOpen(false);
          onOpenChange(false);
          navigate({ to: "/projects" });
        }}
      />
    </Dialog>
  );
}

function OverviewTabHost({ project }: { project: Project }) {
  const [editOpen, setEditOpen] = useState(false);
  return (
    <>
      <OverviewTab project={project} onEditClick={() => setEditOpen(true)} />
      <EditProjectDialog project={project} open={editOpen} onOpenChange={setEditOpen} />
    </>
  );
}

// ── Buy Log Tab ──
// The Buy Log is the GC's procurement workspace (spec: PBOS_BuyLog_Requirements.md).
// Replaces the old read-only budget table — now lives in
// `src/components/buy-log/BuyLogTab.tsx` so the route file stays small.

// ── Commitments Tab ──

function CommitmentsTab({ projectId }: { projectId: string }) {
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = useCommitments({ projectId });
  const deleteCommitment = useDeleteCommitment();
  const [deleteTarget, setDeleteTarget] = useState<Commitment | null>(null);

  if (isLoading) return <Skeleton className="h-64 w-full" />;
  if (isError) return <ErrorState message={error?.message} onRetry={() => refetch()} />;

  // Hide change orders that are still in flight (PCO tab owns those). Approved
  // / voided change orders stay in this list to give the PM a unified view of
  // all authorized contract values on the project.
  const items = (data ?? []).filter(
    (c) =>
      c.commitmentType !== "change_order" ||
      (c.status !== "draft" && c.status !== "pending_approval"),
  );

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            navigate({ to: "/commitments/new", search: { projectId, budgetLineId: "" } })
          }
          className="rounded-lg"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Commitment
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-12 text-center">
          <ClipboardList className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">No commitments found for this project.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <Table>
            <TableHeader className="bg-gray-50">
              <TableRow>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Number
                </TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Type
                </TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Title
                </TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Company
                </TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Status
                </TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500 text-right">
                  Original Amount
                </TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500 text-right">
                  Revised Amount
                </TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((c: Commitment) => (
                <TableRow
                  key={c.id}
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => navigate({ to: "/commitments/$id", params: { id: c.id } })}
                >
                  <TableCell className="text-sm font-medium text-gray-900">
                    <span className="inline-flex items-center gap-1.5">
                      {c.commitmentNumber ?? "--"}
                      {c.commitmentType === "change_order" && c.status === "executed" && (
                        <span className="inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 uppercase tracking-wider">
                          CO
                        </span>
                      )}
                    </span>
                  </TableCell>
                  <TableCell>
                    <TypeBadge label={c.commitmentType} />
                  </TableCell>
                  <TableCell className="text-sm text-gray-900">{c.title ?? "--"}</TableCell>
                  <TableCell className="text-sm text-gray-600">{c.companyName ?? "--"}</TableCell>
                  <TableCell>
                    <StatusBadge status={c.status} />
                  </TableCell>
                  <TableCell className="text-sm text-gray-900 text-right">
                    {formatCurrency(c.originalAmount)}
                  </TableCell>
                  <TableCell className="text-sm text-gray-900 text-right">
                    {formatCurrency(c.revisedAmount)}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() =>
                            navigate({ to: "/commitments/$id/edit", params: { id: c.id } })
                          }
                        >
                          <Pencil className="mr-2 h-4 w-4" />
                          Edit
                        </DropdownMenuItem>
                        {c.status === "draft" && (
                          <DropdownMenuItem
                            className="text-red-600"
                            onClick={() => setDeleteTarget(c)}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title="Delete Commitment"
        description={`Are you sure you want to delete "${deleteTarget?.title ?? ""}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={async () => {
          if (deleteTarget) {
            deleteCommitment.mutate(deleteTarget.id);
            setDeleteTarget(null);
          }
        }}
      />
    </div>
  );
}

// ── RFIs Tab ──

function RfisTab({ projectId }: { projectId: string }) {
  const { data, isLoading, isError, error, refetch } = useRfis(projectId);

  if (isLoading) return <Skeleton className="h-64 w-full" />;
  if (isError) return <ErrorState message={error?.message} onRetry={() => refetch()} />;

  const items = data ?? [];

  if (items.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-12 text-center">
        <ClipboardList className="h-12 w-12 text-gray-300 mx-auto mb-3" />
        <p className="text-sm text-gray-500">No RFIs found for this project.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <Table>
        <TableHeader className="bg-gray-50">
          <TableRow>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              Number
            </TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              Subject
            </TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              Status
            </TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              Priority
            </TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              Submitted By
            </TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              Assigned To
            </TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              Date Submitted
            </TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              Date Required
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((rfi: Rfi) => (
            <TableRow key={rfi.id} className="hover:bg-gray-50">
              <TableCell className="text-sm font-medium text-gray-900">
                {rfi.number ?? "--"}
              </TableCell>
              <TableCell className="text-sm text-gray-900">{rfi.subject ?? "--"}</TableCell>
              <TableCell>
                <StatusBadge status={rfi.status} />
              </TableCell>
              <TableCell className="text-sm text-gray-600">{rfi.priority ?? "--"}</TableCell>
              <TableCell className="text-sm text-gray-600">{rfi.submittedBy ?? "--"}</TableCell>
              <TableCell className="text-sm text-gray-600">{rfi.assignedTo ?? "--"}</TableCell>
              <TableCell className="text-sm text-gray-600">
                {formatDate(rfi.dateSubmitted)}
              </TableCell>
              <TableCell className="text-sm text-gray-600">
                {formatDate(rfi.dateRequired)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// ── Submittals Tab ──

function SubmittalsTab({ projectId }: { projectId: string }) {
  const { data, isLoading, isError, error, refetch } = useSubmittals(projectId);

  if (isLoading) return <Skeleton className="h-64 w-full" />;
  if (isError) return <ErrorState message={error?.message} onRetry={() => refetch()} />;

  const items = data ?? [];

  if (items.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-12 text-center">
        <ClipboardList className="h-12 w-12 text-gray-300 mx-auto mb-3" />
        <p className="text-sm text-gray-500">No submittals found for this project.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <Table>
        <TableHeader className="bg-gray-50">
          <TableRow>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              Number
            </TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              Title
            </TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              Spec Section
            </TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              Status
            </TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              Submitted By
            </TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              Date Submitted
            </TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              Date Required
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((s: Submittal) => (
            <TableRow key={s.id} className="hover:bg-gray-50">
              <TableCell className="text-sm font-medium text-gray-900">
                {s.number ?? "--"}
              </TableCell>
              <TableCell className="text-sm text-gray-900">{s.title ?? "--"}</TableCell>
              <TableCell className="text-sm text-gray-600">{s.specSection ?? "--"}</TableCell>
              <TableCell>
                <StatusBadge status={s.status} />
              </TableCell>
              <TableCell className="text-sm text-gray-600">{s.submittedBy ?? "--"}</TableCell>
              <TableCell className="text-sm text-gray-600">{formatDate(s.dateSubmitted)}</TableCell>
              <TableCell className="text-sm text-gray-600">{formatDate(s.dateRequired)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// ── Team Contacts Tab ──

type ContactSortCol = "name" | "title" | "email" | "phone" | "company";

interface ContactFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  title: string;
  companyId: string;
}

const EMPTY_CONTACT_FORM: ContactFormData = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  title: "",
  companyId: "",
};

const COMPANY_TYPE_GROUPS: {
  key: string;
  label: string;
  matches: (t: string | null) => boolean;
}[] = [
  {
    key: "general_contractor",
    label: "General Contractors",
    matches: (t) => t === "general_contractor",
  },
  {
    key: "subcontractor",
    label: "Subcontractors",
    matches: (t) => t === "subcontractor" || t === "contractor",
  },
  { key: "owner", label: "Owners", matches: (t) => t === "owner" },
  { key: "design_firm", label: "Design Firms", matches: (t) => t === "design_firm" },
  { key: "vendor", label: "Vendors", matches: (t) => t === "vendor" },
  { key: "inspector", label: "Inspectors", matches: (t) => t === "inspector" },
  { key: "legal", label: "Legal", matches: (t) => t === "legal" },
  {
    key: "other",
    label: "Other / Unspecified",
    matches: (t) =>
      !t ||
      ![
        "general_contractor",
        "subcontractor",
        "contractor",
        "owner",
        "design_firm",
        "vendor",
        "inspector",
        "legal",
      ].includes(t),
  },
];

function TeamContactsTab({ projectId }: { projectId: string }) {
  const { data: contacts, isLoading, isError, error, refetch } = useProjectContacts(projectId);
  const { data: companies } = useCompanies();
  const addNewContact = useAddNewContactToProject(projectId);
  const removeContact = useRemoveProjectContact(projectId);

  const [createOpen, setCreateOpen] = useState(false);
  const [existingOpen, setExistingOpen] = useState(false);
  const [viewTarget, setViewTarget] = useState<Contact | null>(null);
  const [viewForm, setViewForm] = useState<ContactFormData>(EMPTY_CONTACT_FORM);
  const updateContact = useUpdateContact();

  function openViewDialog(contact: Contact) {
    setViewTarget(contact);
    setViewForm({
      firstName: contact.firstName ?? "",
      lastName: contact.lastName ?? "",
      email: contact.email ?? "",
      phone: contact.phone ?? "",
      title: contact.title ?? "",
      companyId: contact.companyId ?? "",
    });
  }

  function handleUpdateContact() {
    if (!viewTarget) return;
    updateContact.mutate(
      { id: viewTarget.id, ...viewForm },
      { onSuccess: () => setViewTarget(null) },
    );
  }
  const [formData, setFormData] = useState<ContactFormData>(EMPTY_CONTACT_FORM);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortCol, setSortCol] = useState<ContactSortCol>("name");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [removeTarget, setRemoveTarget] = useState<Contact | null>(null);

  function openCreateDialog() {
    setFormData(EMPTY_CONTACT_FORM);
    setCreateOpen(true);
  }

  function handleSubmit() {
    addNewContact.mutate(
      {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        title: formData.title,
        companyId: formData.companyId,
      },
      { onSuccess: () => setCreateOpen(false) },
    );
  }

  function handleSort(col: ContactSortCol) {
    if (sortCol === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortCol(col);
      setSortDir("asc");
    }
  }

  function SortIcon({ col }: { col: ContactSortCol }) {
    if (sortCol !== col) return null;
    return sortDir === "asc" ? (
      <ChevronUp className="inline h-3 w-3 ml-0.5 opacity-70" />
    ) : (
      <ChevronDown className="inline h-3 w-3 ml-0.5 opacity-70" />
    );
  }

  if (isLoading) return <Skeleton className="h-64 w-full" />;
  if (isError) return <ErrorState message={error?.message} onRetry={() => refetch()} />;

  const q = searchQuery.trim().toLowerCase();
  const filtered = (contacts ?? []).filter((c) => {
    if (!q) return true;
    const haystack = [c.firstName, c.lastName, c.title, c.email, c.phone, c.companyName]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });

  const sorted = [...filtered].sort((a, b) => {
    const dir = sortDir === "asc" ? 1 : -1;
    const nameA = [a.firstName, a.lastName].filter(Boolean).join(" ");
    const nameB = [b.firstName, b.lastName].filter(Boolean).join(" ");
    switch (sortCol) {
      case "name":
        return dir * nameA.localeCompare(nameB);
      case "title":
        return dir * (a.title ?? "").localeCompare(b.title ?? "");
      case "email":
        return dir * (a.email ?? "").localeCompare(b.email ?? "");
      case "phone":
        return dir * (a.phone ?? "").localeCompare(b.phone ?? "");
      case "company":
        return dir * (a.companyName ?? "").localeCompare(b.companyName ?? "");
      default:
        return 0;
    }
  });

  const grouped = COMPANY_TYPE_GROUPS.map((g) => ({
    ...g,
    contacts: sorted.filter((c) => g.matches(c.companyType ?? null)),
  })).filter((g) => g.contacts.length > 0);

  function downloadCSV() {
    const headers = ["Name", "Title", "Email", "Phone", "Company", "Company Type"];
    const rows = sorted.map((c) => [
      [c.firstName, c.lastName].filter(Boolean).join(" "),
      c.title ?? "",
      c.email ?? "",
      c.phone ?? "",
      c.companyName ?? "",
      c.companyType ?? "",
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "contacts.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  const thClass =
    "text-xs font-semibold uppercase tracking-wider text-gray-500 cursor-pointer select-none hover:text-gray-700";

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Input
          placeholder="Search contacts…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="flex-1"
        />
        <Button variant="outline" size="sm" onClick={downloadCSV}>
          <Download className="mr-2 h-4 w-4" />
          Download CSV
        </Button>
        <Button
          variant="outline"
          onClick={() => setExistingOpen(true)}
          title="Pick a contact already in the directory and link them to this project"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Existing
        </Button>
        <Button
          onClick={openCreateDialog}
          title="Adds a brand new contact to the directory and to this project"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add New
        </Button>
      </div>
      <p className="text-xs text-gray-400 -mt-2">
        <span className="font-medium text-gray-500">Add Existing</span> picks from the contact
        directory. <span className="font-medium text-gray-500">Add New</span> creates a brand new
        contact in the directory and adds them to this project.
      </p>

      {sorted.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-gray-400">
          <p className="text-sm">
            {q ? "No contacts match your search." : "No contacts assigned to this project yet."}
          </p>
          {!q && (
            <p className="text-xs mt-1 text-gray-300">
              Use Add Existing or Add New to assign contacts to this project.
            </p>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className={thClass} onClick={() => handleSort("name")}>
                  Name <SortIcon col="name" />
                </TableHead>
                <TableHead className={thClass} onClick={() => handleSort("title")}>
                  Title <SortIcon col="title" />
                </TableHead>
                <TableHead className={thClass} onClick={() => handleSort("email")}>
                  Email <SortIcon col="email" />
                </TableHead>
                <TableHead className={thClass} onClick={() => handleSort("phone")}>
                  Phone <SortIcon col="phone" />
                </TableHead>
                <TableHead className={thClass} onClick={() => handleSort("company")}>
                  Company <SortIcon col="company" />
                </TableHead>
                <TableHead className="w-32 text-xs font-semibold uppercase tracking-wider text-gray-500 text-right">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {grouped.map((group) => (
                <Fragment key={group.key}>
                  <TableRow className="bg-[#415A77]/10 hover:bg-[#415A77]/10 border-b border-[#415A77]/20">
                    <TableCell
                      colSpan={6}
                      className="text-xs font-semibold uppercase tracking-wider text-[#334155] py-2"
                    >
                      {group.label}{" "}
                      <span className="text-[#415A77]/70 font-normal normal-case ml-1">
                        ({group.contacts.length})
                      </span>
                    </TableCell>
                  </TableRow>
                  {group.contacts.map((contact) => (
                    <TableRow key={contact.id} className="hover:bg-gray-50">
                      <TableCell className="text-sm font-medium text-gray-900">
                        {[contact.firstName, contact.lastName].filter(Boolean).join(" ") || "--"}
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {contact.title ?? "--"}
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {contact.email ?? "--"}
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {contact.phone ?? "--"}
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {contact.companyName ?? "--"}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2 text-xs text-gray-500 hover:text-[#415A77] hover:bg-[#415A77]/5"
                            onClick={() => openViewDialog(contact)}
                          >
                            <Eye className="h-3.5 w-3.5 mr-1" />
                            View
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-gray-400 hover:text-red-600 hover:bg-red-50"
                            onClick={() => setRemoveTarget(contact)}
                            aria-label="Remove from project"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <AddExistingContactDialog
        projectId={projectId}
        open={existingOpen}
        onOpenChange={setExistingOpen}
      />

      <Dialog
        open={viewTarget !== null}
        onOpenChange={(open) => {
          if (!open) setViewTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Contact</DialogTitle>
            <DialogDescription>Update the contact details below.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="view-firstName">First Name</Label>
                <Input
                  id="view-firstName"
                  value={viewForm.firstName}
                  onChange={(e) => setViewForm((p) => ({ ...p, firstName: e.target.value }))}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="view-lastName">Last Name</Label>
                <Input
                  id="view-lastName"
                  value={viewForm.lastName}
                  onChange={(e) => setViewForm((p) => ({ ...p, lastName: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="view-email">Email</Label>
                <Input
                  id="view-email"
                  type="email"
                  value={viewForm.email}
                  onChange={(e) => setViewForm((p) => ({ ...p, email: e.target.value }))}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="view-phone">Phone</Label>
                <Input
                  id="view-phone"
                  value={viewForm.phone}
                  onChange={(e) => setViewForm((p) => ({ ...p, phone: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="view-title">Title</Label>
              <Input
                id="view-title"
                value={viewForm.title}
                onChange={(e) => setViewForm((p) => ({ ...p, title: e.target.value }))}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="view-companyId">
                Company <span className="text-destructive">*</span>
              </Label>
              <Select
                value={viewForm.companyId}
                onValueChange={(v) => setViewForm((p) => ({ ...p, companyId: v }))}
              >
                <SelectTrigger id="view-companyId">
                  <SelectValue placeholder="Select company" />
                </SelectTrigger>
                <SelectContent>
                  {(companies ?? []).map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.companyName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewTarget(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdateContact}
              disabled={
                updateContact.isPending ||
                !viewForm.firstName.trim() ||
                !viewForm.lastName.trim() ||
                !viewForm.companyId
              }
            >
              {updateContact.isPending ? "Saving..." : "Update"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={removeTarget !== null}
        onOpenChange={(open) => {
          if (!open) setRemoveTarget(null);
        }}
        title="Remove from project"
        description={`Remove ${[removeTarget?.firstName, removeTarget?.lastName].filter(Boolean).join(" ") || "this contact"} from this project? They will remain in the contact directory.`}
        confirmLabel="Remove"
        variant="destructive"
        onConfirm={async () => {
          if (removeTarget) {
            removeContact.mutate(removeTarget.id);
            setRemoveTarget(null);
          }
        }}
      />

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Contact</DialogTitle>
            <DialogDescription>
              Adds a brand new contact to the directory and to this project. If the email already
              exists in the directory, the existing contact is linked instead.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="contact-firstName">
                  First Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="contact-firstName"
                  value={formData.firstName}
                  onChange={(e) => setFormData((p) => ({ ...p, firstName: e.target.value }))}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="contact-lastName">
                  Last Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="contact-lastName"
                  value={formData.lastName}
                  onChange={(e) => setFormData((p) => ({ ...p, lastName: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="contact-email">Email</Label>
                <Input
                  id="contact-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="contact-phone">Phone</Label>
                <Input
                  id="contact-phone"
                  value={formData.phone}
                  onChange={(e) => setFormData((p) => ({ ...p, phone: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="contact-title">Title</Label>
              <Input
                id="contact-title"
                value={formData.title}
                onChange={(e) => setFormData((p) => ({ ...p, title: e.target.value }))}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="contact-companyId">
                Company <span className="text-destructive">*</span>
              </Label>
              <Select
                value={formData.companyId}
                onValueChange={(v) => setFormData((p) => ({ ...p, companyId: v }))}
              >
                <SelectTrigger id="contact-companyId">
                  <SelectValue placeholder="Select company" />
                </SelectTrigger>
                <SelectContent>
                  {(companies ?? []).map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.companyName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={
                addNewContact.isPending ||
                !formData.firstName.trim() ||
                !formData.lastName.trim() ||
                !formData.companyId
              }
            >
              {addNewContact.isPending ? "Saving..." : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Main Page ──

const PCO_STATUSES = ["draft", "pending_approval"] as const;

function ForbiddenTab() {
  return (
    <div className="rounded-lg border border-border bg-card p-8 text-center">
      <div className="text-sm font-semibold text-muted-foreground">
        This area isn't available to subcontractors
      </div>
      <div className="mt-1 text-xs text-muted-foreground">
        Contact your GC project team if you believe you should have access.
      </div>
    </div>
  );
}

export function ProjectDetailPage() {
  const { projectId } = useParams({ strict: false });
  const pid = projectId ?? "";
  const { data: project, isLoading, isError, error, refetch } = useProject(pid);
  const { data: session } = useSession();
  const isContractor = session?.user?.role === "contractor";

  const navigate = useNavigate({ from: "/projects/$projectId" });
  const rawSearch = useSearch({ strict: false });
  const activeTab = typeof rawSearch.tab === "string" ? rawSearch.tab : "overview";
  const [navCollapsed, setNavCollapsed] = useState(false);

  const { data: milestonesData } = useProjectMilestones(pid);
  const { data: tasksData } = useProjectTasks(pid);
  const { data: contractorsData } = useProjectContractors(pid);
  const { data: projectContactsData } = useProjectContacts(pid);
  const { data: commitmentsData } = useCommitments({ projectId: pid });
  const { data: pcoData } = useChangeOrdersForProject(pid, PCO_STATUSES);
  const { data: docCountData } = useProjectTaskDocumentCount(pid);
  const buyLogSummary = useBuyLogSummary(pid);

  const badges: NavBadges = {
    milestones: milestonesData?.length,
    tasks: tasksData?.length,
    companies: contractorsData?.length,
    contacts: projectContactsData?.length,
    commitments: commitmentsData?.filter(
      (c) =>
        c.commitmentType !== "change_order" ||
        (c.status !== "draft" && c.status !== "pending_approval"),
    ).length,
    pcos: pcoData?.length,
    documents: docCountData?.count,
    buyLog: buyLogSummary.lineCount,
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <ErrorState message={error?.message ?? "Failed to load project"} onRetry={() => refetch()} />
    );
  }

  if (!project) {
    return <ErrorState message="Project not found" />;
  }

  const headerPct = project.percentComplete ? Number(project.percentComplete) : 0;
  const headerIsActive = project.status === "active";

  // Capture the narrowed `project` for use inside the nested renderContent —
  // TS doesn't carry control-flow narrowing into nested function declarations.
  const narrowedProject = project;
  function renderContent() {
    switch (activeTab) {
      case "overview":
        return <OverviewTabHost project={narrowedProject} />;
      case "milestones":
        return isContractor ? <ForbiddenTab /> : <MilestonesTab projectId={pid} />;
      case "tasks":
        return <TasksTab projectId={pid} />;
      case "team-companies":
        return isContractor ? (
          <ProjectDirectoryPanel projectId={pid} />
        ) : (
          <TeamTab projectId={pid} />
        );
      case "team-contacts":
        return <TeamContactsTab projectId={pid} />;
      case "pcos":
        return <PcoTab projectId={pid} />;
      case "commitments":
        return <CommitmentsTab projectId={pid} />;
      case "buy-log":
        return isContractor ? <ForbiddenTab /> : <BuyLogTab projectId={pid} />;
      case "sov":
        return isContractor ? <ForbiddenTab /> : <SoVTab projectId={pid} />;
      case "gr-sov":
        return isContractor ? <ForbiddenTab /> : <GrSovTab projectId={pid} />;
      case "pay-applications":
        return <PayApplicationsTab projectId={pid} />;
      case "finance":
        return isContractor ? null : <GcCostSetupTab projectId={pid} />;
      case "rfis":
        return <RfisTab projectId={pid} />;
      case "submittals":
        return <SubmittalsTab projectId={pid} />;
      case "documents":
        return <DocumentsTab projectId={pid} />;
      case "site-photos":
        return <ProjectSitePhotosTab projectId={pid} />;
      default:
        return <OverviewTabHost project={narrowedProject} />;
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Page Header — sticky */}
      <div className="sticky top-0 z-20 bg-[#f9fafb] border-b border-gray-200 pb-2 md:pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-2 md:gap-3 min-w-0">
            <Link to="/projects">
              <button
                type="button"
                className="flex items-center justify-center h-8 w-8 md:h-9 md:w-9 rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-[#334155] transition-colors shrink-0"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
            </Link>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-lg md:text-2xl font-bold text-[#334155] truncate">
                  {project.name}
                </h1>
                {headerIsActive ? (
                  <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700 shrink-0">
                    <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                    Active
                  </span>
                ) : (
                  <StatusBadge status={project.status} />
                )}
              </div>
              {/* Subtitle — hidden on very small screens */}
              <div className="mt-0.5 hidden sm:flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-gray-500">
                {project.projectNumber ? (
                  <span className="font-mono text-gray-600">{project.projectNumber}</span>
                ) : null}
                {project.client ? (
                  <>
                    <span className="text-gray-300">·</span>
                    <span>
                      <span className="text-gray-400">Owner:</span>{" "}
                      <span className="text-gray-700">{project.client}</span>
                    </span>
                  </>
                ) : null}
                {project.location ? (
                  <>
                    <span className="text-gray-300">·</span>
                    <span className="truncate">{project.location}</span>
                  </>
                ) : null}
              </div>
            </div>
          </div>

          {/* Stats — desktop: right-aligned column pair; hidden on mobile (shown below) */}
          <div className="hidden sm:flex items-start gap-6 text-right shrink-0">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-gray-400">
                Contract Value
              </div>
              <div className="text-lg font-semibold text-[#334155] tabular-nums">
                {formatCurrency(project.contractValue)}
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-gray-400">Schedule</div>
              <div className="flex items-center gap-2 justify-end">
                <div className="w-20">
                  <ProgressBar percent={headerPct} />
                </div>
                <div className="text-lg font-semibold text-[#334155] tabular-nums">
                  {headerPct}%
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile stats row — below title, above tab bar */}
        <div className="flex sm:hidden items-center gap-4 mt-1.5 pl-10">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-gray-400">Contract Value</div>
            <div className="text-sm font-semibold text-[#334155] tabular-nums">
              {formatCurrency(project.contractValue)}
            </div>
          </div>
          <div className="h-6 w-px bg-gray-200" />
          <div>
            <div className="text-[10px] uppercase tracking-wider text-gray-400">Schedule</div>
            <div className="flex items-center gap-1.5">
              <div className="w-14">
                <ProgressBar percent={headerPct} />
              </div>
              <div className="text-sm font-semibold text-[#334155] tabular-nums">{headerPct}%</div>
            </div>
          </div>
        </div>
      </div>

      {/* Nav + Content
          Mobile:  flex-col — tab bar on top (inside ProjectDetailNav), content below
          Desktop: flex-row — sidebar on left, content on right with independent scroll */}
      <div className="flex flex-col flex-1 md:flex-row md:min-h-0 md:pt-6">
        <ProjectDetailNav
          activeTab={activeTab}
          onTabChange={(tab) => navigate({ search: (prev) => ({ ...prev, tab }), replace: true })}
          badges={badges}
          isContractor={isContractor}
          collapsed={navCollapsed}
          onToggleCollapse={() => setNavCollapsed((v) => !v)}
        />
        <div className="flex-1 min-w-0 px-0 pt-4 pb-8 md:pl-8 md:pt-0 md:pb-0 md:overflow-y-auto">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}
